import { sortBy, sum, uniq } from "lodash";
import flatten from "lodash/flatten";
import { createSelector } from "reselect";

import {
  EidTransferStatus,
  NLISFileTypes,
  TransferStatus,
} from "constants/nlis";

import { EMPTY_OBJECT } from "lib";

import { pluralize, uniqOrCount } from "lib/pluralize";
import { formatDateTimeUTC } from "lib/timeFormats";

import {
  selectBuyerDisplayNameBySaleLotIdLookup,
  selectVendorNameBySaleLotIdLookup,
} from "./businesses";
import {
  selectVendorIdByConsignmentIdLookup,
  selectSaleLotIdsByConsignmentIdLookup,
} from "./indexes";
import { createLookupCombiner } from "./lib";
import {
  getBusinesses,
  getP2PFiles,
  getConsignments,
  getExportP2PInFiles,
  getExportP2PInReversals,
  getExportP2POutFiles,
  getExportP2POutReversals,
  getNLISFiles,
  getProperties,
  getSaleLots,
  getSellFiles,
  getSellReversals,
  getTakeFiles,
  getTakeReversals,
  getP2PReversals,
  getScans,
  getSightingFiles,
} from "./root";

export const selectSellFileBySaleLotIdLookup = createSelector(
  [getSellFiles],
  sellFiles =>
    sortBy(Object.values(sellFiles), "created")
      .reverse()
      .reduce((acc, sf) => {
        (sf.saleLots || []).forEach(saleLotId => {
          if (!acc[saleLotId]) {
            acc[saleLotId] = [sf];
          } else {
            acc[saleLotId].push(sf);
          }
        });
        return acc;
      }, {}),
);

export const getSellFileBySaleLotId = (sellFiles, saleLotId) => {
  if (Array.isArray(sellFiles[saleLotId]) && sellFiles[saleLotId].length > 0) {
    return sellFiles[saleLotId][0];
  }
  return null;
};

export const selectTakeFilesByConsignmentIdLookup = createSelector(
  [getConsignments, getTakeFiles],
  (consignments, takeFiles) =>
    sortBy(Object.values(takeFiles), "created")
      .reverse()
      .reduce(
        (acc, tf) => {
          tf.consignments.forEach(consignmentId => {
            if (!acc[consignmentId]) {
              acc[consignmentId] = [tf];
            } else {
              acc[consignmentId].push(tf);
            }
          });
          return acc;
        },
        Object.keys(consignments).reduce((acc, consignmentId) => {
          acc[consignmentId] = [];
          return acc;
        }, {}),
      ),
);

export const selectTakeReversalByTakeFileIdLookup = createSelector(
  [getTakeReversals],
  takeReversals =>
    Object.values(takeReversals).reduce((acc, tr) => {
      acc[tr.reversalOf] = tr;
      return acc;
    }, {}),
);

export const selectTakeReversalByTakeFileId = takeFileId => state =>
  selectTakeReversalByTakeFileIdLookup(state)[takeFileId] || null;

export const getTakeFilesWithExtraInfo = createSelector(
  [selectTakeFilesByConsignmentIdLookup, selectTakeReversalByTakeFileIdLookup],
  (takeFiles, takeReversals) => {
    const completedCalcs = {};
    return Object.entries(takeFiles).reduce(
      (takeFileObj, [id, theseTakeFiles]) => {
        const takeFileArray = Array.isArray(theseTakeFiles)
          ? theseTakeFiles
          : [theseTakeFiles];
        if (takeFileArray) {
          takeFileObj[id] = takeFileArray.map(takeFile => {
            if (!completedCalcs[takeFile.id]) {
              const eids = flatten(
                [].concat(
                  (takeFile.transfers || []).map(transfer =>
                    transfer.eids.map(eid => eid.EID),
                  ),
                ),
              );
              const eidErrors = flatten(
                [].concat(
                  (takeFile.transfers || []).map(transfer =>
                    transfer.eids.filter(
                      eid => eid.response_type === EidTransferStatus.ERROR,
                    ),
                  ),
                ),
              );

              const eidWarnings = flatten(
                [].concat(
                  (takeFile.transfers || []).map(transfer =>
                    transfer.eids.filter(
                      eid => eid.response_type === EidTransferStatus.WARNING,
                    ),
                  ),
                ),
              );
              const takeReversal = takeReversals[takeFile.id];
              const eidCount =
                takeFile.pending ||
                takeFile.status === TransferStatus.UNSUBMITTED
                  ? "-"
                  : eids.length;
              completedCalcs[takeFile.id] = Object.assign({}, takeFile, {
                consignmentCount: takeFile.consignments
                  ? takeFile.consignments.length
                  : 0,
                eids,
                eidErrors,
                eidWarnings,
                eidCount,
                formattedCreated: takeFile.created
                  ? formatDateTimeUTC(takeFile.created)
                  : "-",
                formattedSubmitted: takeFile.submitted
                  ? formatDateTimeUTC(takeFile.submitted)
                  : "-",
                formattedProcessed: takeFile.processed
                  ? formatDateTimeUTC(takeFile.processed)
                  : "-",
                takeReversal,
              });
            }
            return completedCalcs[takeFile.id];
          });
        }

        return takeFileObj;
      },
      {},
    );
  },
);

export const selectReversableTakeFileIds = createSelector(
  [
    getTakeFiles,
    selectSaleLotIdsByConsignmentIdLookup,
    selectSellFileBySaleLotIdLookup,
  ],
  (takeFiles, saleLotIdsByConsignmentIdLookup, sellFiles) =>
    // A take file is reversable if:
    // it is success (XXX - or warning?)
    // None of the sale lots, within the consignments, are SOLD (ie, sell files exist, and
    // latest sell file is in any state other than rolled back XXX)
    Object.values(takeFiles)
      .filter(takeFile => {
        if (takeFile.status !== TransferStatus.SUCCESS) {
          return false;
        }
        // Rummage out the take files, for the sale lots, for each of these consignments; if they
        // exist, and are not rolled back (XXX failed?) allow rollback of the corresponding take.
        return (
          (takeFile.consignments || []).filter(
            consignmentId =>
              saleLotIdsByConsignmentIdLookup[consignmentId].filter(
                saleLotId =>
                  !sellFiles[saleLotId] ||
                  sellFiles[saleLotId].length === 0 ||
                  sortBy(sellFiles[saleLotId], "created").reverse()[0]
                    .status === TransferStatus.ROLLED_BACK,
              ).length === 0,
          ).length === 0
        );
      })
      .map(takeFile => takeFile.id),
);

export const getIsTakeFileReversable = takeFileId => state => {
  return selectReversableTakeFileIds(state).includes(takeFileId);
};

export const selectSellReversalBySellFileIdLookup = createSelector(
  [getSellReversals],
  sellReversals =>
    Object.values(sellReversals).reduce((acc, tr) => {
      acc[tr.reversalOf] = tr;
      return acc;
    }, {}),
);

export const selectSellReversalBySellFileId = sellFileId => state =>
  selectSellReversalBySellFileIdLookup(state)[sellFileId] || null;

export const selectReversableSellFileIds = createSelector(
  [getSellFiles],
  sellFiles =>
    Object.values(sellFiles)
      .filter(sellFile => sellFile.status === TransferStatus.SUCCESS)
      .map(sellFile => sellFile.id),
);

export const getIsSellFileReversable = sellFileId => state => {
  return selectReversableSellFileIds(state).includes(sellFileId);
};

const getTakeFileVendorDisplay = (
  consignments,
  vendorIdByConsignmentIdLookup,
  businesses,
) => {
  if (consignments) {
    if (consignments.length > 1) {
      return `${consignments.length} Consignments`;
    } else {
      const vendorId = vendorIdByConsignmentIdLookup[consignments[0]];
      const vendorName = businesses[vendorId]?.name;
      return [consignments[0]?.vendorNumber, vendorName]
        .filter(Boolean)
        .join(" ");
    }
  } else {
    return undefined;
  }
};

const getSellFileBuyerDisplay = (
  saleLots,
  buyerDisplayNameBySaleLotIdLookup,
  noun = "Buyer",
) => {
  let buyerDisplay = "";
  if (saleLots) {
    const numLots = saleLots.length;
    const lotText = `${numLots} ${pluralize("Lot", numLots)}`;
    const buyers = uniq(
      saleLots.map(saleLotId => buyerDisplayNameBySaleLotIdLookup[saleLotId]) ||
        [],
    );
    const numBuyers = buyers.length;

    if (numBuyers === 1) {
      buyerDisplay = `${buyers[0]} : ${lotText}`;
    } else {
      buyerDisplay = `${numBuyers} ${pluralize(noun, numBuyers)} : ${lotText}`;
    }
  }
  return buyerDisplay;
};

const formatNLISFile =
  (
    vendorIdByConsignmentIdLookup,
    businesses,
    nlisFileLookup,
    buyerDisplayNameBySaleLotIdLookup = {},
  ) =>
  nlisFile => {
    return Object.assign({}, nlisFile, {
      eidCount: sum(nlisFile.transfers?.map(t => t.eids.length)),
      eidSuccessCount: nlisFile.transfers?.reduce((acc, transfer) => {
        acc += transfer.eids.filter(
          e => e.response_type === EidTransferStatus.SUCCESS,
        ).length;
        return acc;
      }, 0),
      vendorDisplay: getTakeFileVendorDisplay(
        nlisFile.consignments,
        vendorIdByConsignmentIdLookup,
        businesses,
      ),
      buyerDisplay: getSellFileBuyerDisplay(
        nlisFile.saleLots,
        buyerDisplayNameBySaleLotIdLookup,
      ),
      relatedTransaction: nlisFile.reversalOf
        ? nlisFileLookup[nlisFile.reversalOf]
        : null,
    });
  };

export const selectNLISTransactionByTypeByIdLookup = createSelector(
  [getNLISFiles, selectVendorIdByConsignmentIdLookup, getBusinesses],
  (nlisFiles, vendorIdByConsignmentIdLookup, businesses) => {
    const formatFunction = formatNLISFile(
      vendorIdByConsignmentIdLookup,
      businesses,
      nlisFiles,
    );

    return Object.values(nlisFiles).reduce((acc, cur) => {
      Object.values(cur.byId).forEach(nlisFile => {
        if (acc[nlisFile.transferType]) {
          acc[nlisFile.transferType][nlisFile.id] = formatFunction(nlisFile);
        } else {
          acc[nlisFile.transferType] = {
            [nlisFile.id]: formatFunction(nlisFile),
          };
        }
      });
      return acc;
    }, {});
  },
);

export const getNLISTransactionByTypeById = (type, id) => state => {
  return (
    selectNLISTransactionByTypeByIdLookup(state)?.[type]?.[id] || EMPTY_OBJECT
  );
};

// A file can only roll back once its next leg has rolled back or failed.
// Used by Take (blocked by a live Sell) and Export P2P In (blocked by a
// live P2P Out).
const createForwardLegRollbackCombiner = nextLegLatestFileIdField =>
  createLookupCombiner((selfFile, nextLegFileByIdLookup, scanByEidLookup) => {
    const transfers = selfFile.transfers || [];
    const isNextLegClear = transfers.every(nlisTransfer =>
      nlisTransfer.eids.every(nlisTransferEid => {
        const scan = scanByEidLookup[nlisTransferEid.EID] || {};
        const nextLegFileId = scan[nextLegLatestFileIdField];
        if (!nextLegFileId) {
          return true;
        }
        const nextLegFile = nextLegFileByIdLookup[nextLegFileId];
        return (
          nextLegFile &&
          (nextLegFile.status === TransferStatus.ROLLED_BACK ||
            nextLegFile.status === TransferStatus.FAILED)
        );
      }),
    );
    return selfFile.status === TransferStatus.SUCCESS && isNextLegClear;
  });

export const selectIsRollbackAvailableByTypeById = createSelector(
  [
    getTakeFiles,
    getTakeReversals,
    getSellFiles,
    getSellReversals,
    getP2PFiles,
    getP2PReversals,
    getExportP2PInFiles,
    getExportP2PInReversals,
    getExportP2POutFiles,
    getExportP2POutReversals,
    getScans,
  ],
  (
    nlisTakeFileByIdLookup,
    nlisTakeReversalFileByIdLookup,
    nlisSellFileByIdLookup,
    nlisSellReversalFileByIdLookup,
    nlisP2PTransferFileByIdLookup,
    nlisP2PTransferReversalFileByIdLookup,
    nlisExportP2PInFileByIdLookup,
    nlisExportP2PInReversalFileByIdLookup,
    nlisExportP2POutFileByIdLookup,
    nlisExportP2POutReversalFileByIdLookup,
    scanByEidLookup,
  ) => ({
    // Rollable when no Sell File exists yet, or it's rolled back/errored.
    [NLISFileTypes.NLISTakeFile]: createForwardLegRollbackCombiner(
      "latest_sell_file_id",
    )(nlisTakeFileByIdLookup, nlisSellFileByIdLookup, scanByEidLookup),
    [NLISFileTypes.NLISTakeReversalFile]: createLookupCombiner(
      // NLISTakeReversalFile are not able to be rolled back
      () => false,
    )(nlisTakeReversalFileByIdLookup),
    [NLISFileTypes.NLISSellFile]: createLookupCombiner(
      nlisSellFile =>
        // An NLIS Sell File can only be rolled back when the transfer was successful
        nlisSellFile.status === TransferStatus.SUCCESS,
    )(nlisSellFileByIdLookup),
    [NLISFileTypes.NLISSellReversalFile]: createLookupCombiner(
      // NLISSellReversalFile are not able to be rolled back
      () => false,
    )(nlisSellReversalFileByIdLookup),
    [NLISFileTypes.NLISP2PTransferFile]: createLookupCombiner(
      nlisP2PTransferFile =>
        // An NLIS P2P Transfer File can only be rolled back when the transfer was successful
        nlisP2PTransferFile.status === TransferStatus.SUCCESS,
    )(nlisP2PTransferFileByIdLookup),
    [NLISFileTypes.NLISP2PTransferReversalFile]: createLookupCombiner(
      // NLISP2PTransferReversalFile are not able to be rolled back
      () => false,
    )(nlisP2PTransferReversalFileByIdLookup),
    // Rollable when no P2P Out file exists yet, or it's rolled back/failed -
    // otherwise NLIS would show the animal as never arrived while its own
    // ledger says it already left.
    [NLISFileTypes.NLISExportP2PInFile]: createForwardLegRollbackCombiner(
      "latest_export_p2p_out_file_id",
    )(
      nlisExportP2PInFileByIdLookup,
      nlisExportP2POutFileByIdLookup,
      scanByEidLookup,
    ),
    [NLISFileTypes.NLISExportP2PInReversalFile]: createLookupCombiner(
      () => false,
    )(nlisExportP2PInReversalFileByIdLookup),
    [NLISFileTypes.NLISExportP2POutFile]: createLookupCombiner(
      nlisExportP2POutFile =>
        nlisExportP2POutFile.status === TransferStatus.SUCCESS,
    )(nlisExportP2POutFileByIdLookup),
    [NLISFileTypes.NLISExportP2POutReversalFile]: createLookupCombiner(
      () => false,
    )(nlisExportP2POutReversalFileByIdLookup),
  }),
);

export const getIsRollbackAvailableByTypeById = (type, id) => state =>
  selectIsRollbackAvailableByTypeById(state)[type]?.[id] || false;

export const selectTransferOutTransactions = createSelector(
  [
    getSellFiles,
    getSellReversals,
    selectVendorIdByConsignmentIdLookup,
    getBusinesses,
    selectBuyerDisplayNameBySaleLotIdLookup,
  ],
  (
    sellFiles,
    sellReversals,
    vendorIdByConsignmentIdLookup,
    businesses,
    buyerDisplayNameBySaleLotIdLookup,
  ) => {
    const formatFunction = formatNLISFile(
      vendorIdByConsignmentIdLookup,
      businesses,
      sellFiles,
      buyerDisplayNameBySaleLotIdLookup,
    );
    return [].concat
      .apply([], [Object.values(sellFiles), Object.values(sellReversals)])
      .map(formatFunction);
  },
);

export const selectTransferInTransactions = createSelector(
  [
    getTakeFiles,
    getTakeReversals,
    selectVendorIdByConsignmentIdLookup,
    getBusinesses,
  ],
  (takeFiles, takeReversals, vendorIdByConsignmentIdLookup, businesses) => {
    const formatFunction = formatNLISFile(
      vendorIdByConsignmentIdLookup,
      businesses,
      takeFiles,
    );

    return [].concat
      .apply([], [Object.values(takeFiles), Object.values(takeReversals)])
      .map(formatFunction);
  },
);

export const selectSightingTransactions = createSelector(
  [getSightingFiles],
  sightingFiles =>
    Object.values(sightingFiles).map(sf => ({
      ...sf,
      eidCount: sf.sightings.length,
      eidSuccessCount: sf.sightings.filter(
        e => e.status === EidTransferStatus.SUCCESS,
      ).length,
    })),
);

// Out's counterparty is a destination property, not a real Buyer, so a
// sale lot may have no buyer_id - buyerDisplay can't stand in here.
const selectDestinationDisplayNameBySaleLotIdLookup = createSelector(
  [getSaleLots, getProperties],
  (saleLots, properties) =>
    Object.entries(saleLots).reduce((acc, [saleLotId, saleLot]) => {
      const property = properties[saleLot.destination_property_id];
      if (property) {
        acc[saleLotId] = property.name || property.PIC;
      }
      return acc;
    }, {}),
);

// Shared vendor/buyer/EID count display for generic P2P and both Export
// P2P legs.
const buildP2PTransactionsSelector = (
  getFiles,
  getReversals,
  // Generic P2P already mislabels this "Buyer" - kept as-is, out of
  // scope to fix here.
  vendorCountLabel,
) =>
  createSelector(
    [
      getFiles,
      getReversals,
      selectBuyerDisplayNameBySaleLotIdLookup,
      selectVendorNameBySaleLotIdLookup,
      selectDestinationDisplayNameBySaleLotIdLookup,
    ],
    (
      files,
      reversals,
      buyerDisplayNameBySaleLotIdLookup,
      vendorNameBySaleLotIdLookup,
      destinationDisplayNameBySaleLotIdLookup,
    ) => {
      const formatFunction = transferFile => {
        const file =
          (transferFile.reversalOf
            ? files[transferFile.reversalOf]
            : transferFile) || {};
        const transfers = file.transfers || [];
        const saleLots = file.saleLots || [];

        const transferEids = flatten(transfers.map(transfer => transfer.eids));

        return {
          ...transferFile,
          vendorDisplay: uniqOrCount(
            saleLots.map(saleLotId => vendorNameBySaleLotIdLookup[saleLotId]),
            vendorCountLabel,
          ),
          buyerDisplay: getSellFileBuyerDisplay(
            saleLots,
            buyerDisplayNameBySaleLotIdLookup,
          ),
          destinationDisplay: getSellFileBuyerDisplay(
            saleLots,
            destinationDisplayNameBySaleLotIdLookup,
            "Destination",
          ),
          eidCount: transferEids.length,
          eidSuccessCount: transferEids.filter(
            e => e.response_type === EidTransferStatus.SUCCESS,
          ).length,
        };
      };
      return [].concat
        .apply([], [Object.values(files), Object.values(reversals)])
        .map(formatFunction);
    },
  );

export const selectP2PTransactions = buildP2PTransactionsSelector(
  getP2PFiles,
  getP2PReversals,
  "Buyer",
);

export const selectExportP2PInTransactions = buildP2PTransactionsSelector(
  getExportP2PInFiles,
  getExportP2PInReversals,
  "Vendor",
);

export const selectExportP2POutTransactions = buildP2PTransactionsSelector(
  getExportP2POutFiles,
  getExportP2POutReversals,
  "Vendor",
);
